import { matchPath } from 'react-router-dom';

// Static page metadata for the topbar page-title slot.
// Routes whose title is dynamic (portfolio/project name, gate id, etc.)
// call usePageMetadata({title, subtitle}) inside the page component instead
// of relying on this map.
//
// Page contract (05 §6, AC-UX-1): the `subtitle` doubles as the page's
// plain-language PURPOSE LINE rendered beneath the title in the topbar.
// Every entry must carry a non-empty title and purpose line — enforced by
// __tests__/pageMetadata.test.js. Legacy alias URLs (LEGACY_ROUTE_ALIASES)
// never render a page, so they must not appear here.

export const STATIC_PAGE_METADATA = Object.freeze({
  '/home': {
    title: 'Mission Brief',
    subtitle: "Pick up where you left off, or drive into today's biggest signals.",
  },
  '/executive': {
    title: 'Executive Command Center',
    subtitle:
      'Portfolio health, mission impact, and predictive alerts grounded in audited evidence.',
  },
  '/project-factory': {
    title: 'Intake Queue',
    subtitle:
      'The OWHA intake + lifecycle factory: review, score, route, and generate project records from OWHA intake requests.',
  },
  '/loe-dashboard': {
    title: 'Director LOE Portfolio Dashboard',
    subtitle: 'LOE-aligned intake, active delivery, and action rollups for DHA leadership.',
  },
  '/actions': {
    title: 'My Action Center',
    subtitle: 'Assigned, overdue, watched, and mentioned actions across OWHA delivery.',
  },
  '/meetings': {
    title: 'Meeting Manager',
    subtitle: 'Meeting records, decisions, and draft-action extraction for OWHA collaboration.',
  },
  '/knowledge': {
    title: 'Knowledge Library',
    subtitle: 'Artifacts, lessons learned, closeout packages, and decision rationale across OWHA.',
  },
  '/resources': {
    title: 'Resource Command Center',
    subtitle: 'Capacity conflicts, LOE staffing posture, and assignment pressure across OWHA work.',
  },
  '/resources/skills': {
    title: 'Skills Matrix',
    subtitle: 'Verified capability, proficiency, and access posture across OWHA resource pools.',
  },
  '/resources/allocations/:id': {
    title: 'Allocation Conflict Detail',
    subtitle: 'Inspect impacted projects and work items before reassignment.',
  },
  '/resources/assignments/new': {
    title: 'Assignment Wizard',
    subtitle:
      'Recommend resources for approved demand with skill, access, and capacity constraints.',
  },
  '/timesheets': {
    title: 'Timesheet Home',
    subtitle: 'Current weekly submission plus recent review outcomes and approval posture.',
  },
  '/timesheets/current': {
    title: 'Current Weekly Timesheet',
    subtitle:
      'Weekly grid for logging time against projects, work items, actions, and meeting-derived drafts.',
  },
  '/governance': {
    title: 'Governance Command Center',
    subtitle: 'Risk, issue, change, and EVM posture for OWHA project delivery.',
  },
  '/governance/risks': {
    title: 'Risk Register',
    subtitle: 'Probability, impact, and mitigation posture across OWHA projects.',
  },
  '/governance/issues': {
    title: 'Issue Log',
    subtitle: 'Tracked blockers that have been promoted for operational resolution.',
  },
  '/governance/changes': {
    title: 'Change Requests',
    subtitle: 'Governance review for schedule, cost, and staffing deltas before execution.',
  },
  '/governance/evm': {
    title: 'PMO / EVM Dashboard',
    subtitle: 'Explainable variance, CPI/SPI posture, and health-check review state.',
  },
  '/decision-support': {
    title: 'Strategic Decision Workspace',
    subtitle: 'Compare COAs, model scenarios, and capture auditable rationale.',
  },
  '/lifecycle': {
    title: 'Portfolio Lifecycle Pipeline',
    subtitle: 'Initiative flow from intake through retirement.',
  },
  '/delivery': {
    title: 'Delivery Workspace',
    subtitle: 'Sprint health, work items, and flow across active initiatives.',
  },
  '/roadmap': {
    title: 'Strategic Roadmap',
    subtitle: 'Initiative timeline and milestone view across the portfolio.',
  },
  '/financials': {
    title: 'Financial Lifecycle Manager',
    subtitle: 'Budget vs actual vs forecast, funding mix, and lifecycle money flow.',
  },
  '/financials/adjustments': {
    title: 'Forecast Adjustments',
    subtitle: 'Pending review items, rationale, and official review-state tracking.',
  },
  '/financials/funding-sources': {
    title: 'Funding Sources',
    subtitle: 'Appropriation posture, execution visibility, and connector freshness.',
  },
  '/rapids': {
    title: 'RAPID Command Center',
    subtitle:
      'Native PRIME workflow for intake, scoring, pod execution, value proof, and graduation.',
  },
  '/ai/ask': {
    title: 'Ask PRIME',
    subtitle: 'Grounded Q&A with citations to the underlying evidence.',
  },
  '/ai/orchestration': {
    title: 'AI Orchestration Hub',
    subtitle: 'Human review queue and telemetry for AI-drafted outputs.',
  },
  '/admin/rbac': {
    title: 'Role & Permission Matrix',
    subtitle: 'Auditable access inspection behind every persona.',
  },
  '/admin/site-access': {
    title: 'Who can reach PMHub',
    subtitle: 'Approve, deny, suspend, or revoke individual CAC holders before they reach the app.',
  },
  '/admin/data': {
    title: 'Connectors',
    subtitle: 'Data feeds, resource domains, and source-system health for DHA PRIME.',
  },
  '/admin/custom-fields': {
    title: 'Configuration',
    subtitle: 'Custom fields, forms, rubrics, and LOE metadata for DHA PRIME.',
  },
  '/admin/forms': {
    title: 'Form Builder',
    subtitle: 'Versioned configuration of intake, closeout, and UAT form surfaces.',
  },
  '/admin/rubrics': {
    title: 'Rubric Admin',
    subtitle: 'Weighted scoring criteria and approval-gated rubric activation for DHA PRIME.',
  },
  '/admin/ingestion': {
    title: 'Ingestion Center',
    subtitle: 'Upload, map, validate, and commit governed source data into DHA PRIME.',
  },
  '/admin/lineage': {
    title: 'Lineage',
    subtitle: 'Source systems, evidence movement, and data-quality handoffs across the ecosystem.',
  },
  '/admin/compliance': {
    title: 'Compliance Readiness',
    subtitle: 'Security posture, readiness evidence, and operational support for DHA PRIME.',
  },
  '/search': {
    title: 'Search results',
    subtitle: 'Portfolios, programs, projects, and briefings matching your query.',
  },
  '/feedback': {
    title: 'My Feedback',
    subtitle: 'Inspect and manage Drafts, Submitted, and Resolved feedback from one workspace.',
  },
  '/feedback/submitted/:packageId': {
    title: 'Feedback sent',
    subtitle: 'Your requests are with the PRIME product team.',
  },
  // Typed help/intake framework, commit 2. The exact-match entry is resolved
  // before the pattern below, so /feedback/support/new never reads as a draft id.
  '/feedback/support/new': {
    title: 'Report a problem',
    subtitle: 'Send a GSC ticket to the MHS Service Desk and keep a tracked copy in DHA PRIME.',
  },
  '/feedback/support/:intakeId': {
    title: 'Support ticket',
    subtitle: 'Finish, review, or re-read a support ticket you started in DHA PRIME.',
  },
  // Typed help/intake framework, commit 4. Exact-match entry resolved before the
  // pattern below, so /feedback/request/new never reads as a draft id.
  '/feedback/request/new': {
    title: 'Request a feature or service',
    subtitle:
      'Answer a few guided questions, confirm PRIME’s draft analysis, and send the request to the product team.',
  },
  '/feedback/request/:intakeId': {
    title: 'Feature or service request',
    subtitle:
      'Continue a request you started, check its preliminary analysis, or re-read what you sent.',
  },
  '/feedback/queue': {
    title: 'Feedback Queue',
    subtitle: 'Governed UI feedback triage, conversion, and audit posture for OWHA adoption.',
  },
  '/feedback/workspace': {
    title: 'My Feedback',
    subtitle: 'Legacy workspace links return here unless an editor session is active.',
  },
  '/feedback/:packageId/edit': {
    title: 'Feedback workspace',
    subtitle:
      'Resume a saved draft, annotate captured evidence, and prepare it for review and send.',
  },
  '/feedback/:packageId': {
    title: 'Feedback history',
    subtitle: 'Replay the submitted evidence exactly as it was sent, without any edit path.',
  },
  '/uat': {
    title: 'UAT Dashboard',
    subtitle:
      'Cohort readiness, satisfaction attainment, and onboarding proof for bake-off acceptance.',
  },
  '/products': {
    title: 'Briefing Product Factory',
    subtitle:
      'Generate governed executive decks, papers, reports, and export-ready briefing products.',
  },
  '/status-packs': {
    title: 'Status Pack Library',
    subtitle: 'Publish sanitized, read-only stakeholder status packs from approved PRIME records.',
  },
  '/portfolios': {
    title: 'Portfolios',
    subtitle: 'Browse every portfolio with rollup health, spend, and delivery posture.',
  },
  '/programs': {
    title: 'Programs',
    subtitle: 'Browse programs across portfolios with schedule, spend, and risk rollups.',
  },
  '/projects': {
    title: 'Projects',
    subtitle: 'Track every project with gate posture, health, and delivery status in one register.',
  },
  '/project-factory/requests/new': {
    title: 'New OWHA Intake Request',
    subtitle: 'Submit a Project Intake Form to start scoring, routing, and review.',
  },
  '/knowledge/lessons-learned': {
    title: 'Lessons Learned Library',
    subtitle: 'Reusable lessons captured from completed OWHA work, with provenance.',
  },
  '/market-research': {
    title: 'Market Research Tracker',
    subtitle: 'Capability findings and vendor comparisons that inform course-of-action decisions.',
  },
  '/notifications': {
    title: 'Inbox',
    subtitle: 'Alerts, review requests, and updates addressed to you across DHA PRIME.',
  },
  '/activity': {
    title: 'My Activity',
    subtitle: 'A record of the actions you have taken across DHA PRIME.',
  },
  '/saved-views': {
    title: 'Saved Views',
    subtitle: 'Return to the filtered views you saved across PRIME workspaces.',
  },
  '/access/status': {
    title: 'My Access',
    subtitle: 'Where your access requests stand and what you can use today.',
  },
  '/access/request': {
    title: 'Request Access',
    subtitle: 'Ask for the roles and scopes you need — requests route to approvers.',
  },
  '/me': {
    title: 'My Profile',
    subtitle: 'Your roles, alignment, and preferences in one place.',
  },
  '/settings': {
    title: 'Settings',
    subtitle: 'Notification, display, and account preferences for your PRIME sessions.',
  },
  '/help': {
    title: 'Help & Getting Started',
    subtitle: 'Learn what PRIME is, take a tour, and find guides for your role.',
  },
  '/help/glossary': {
    title: 'Glossary',
    subtitle: 'Plain-language definitions for every term and acronym used in PRIME.',
  },
  '/help/capability-map': {
    title: 'Capability Map',
    subtitle: 'See what PRIME delivers for each mission objective — and what comes as a bonus.',
  },
  // Typed help/intake framework, commit 5.
  '/help/questions': {
    title: 'Ask a Question',
    subtitle:
      'PRIME answers from its own knowledge base where it can, and sends your question to a person where it cannot.',
  },
  '/admin/access-requests': {
    title: 'Access Reviews',
    subtitle: 'Approve or deny pending access requests with an auditable decision trail.',
  },
  '/admin/users': {
    title: 'User Administration',
    subtitle:
      'Create managed accounts, issue temporary passwords, and control password or CAC sign-in posture across DHA PRIME.',
  },
  '/admin/authentication': {
    title: 'Authentication Controls',
    subtitle:
      'Switch the live sign-in mode, confirm environment locks, and review recent auth-control activity.',
  },
  '/admin/audit': {
    title: 'Audit Log',
    subtitle: 'Immutable record of who changed what, when, and from where.',
  },
  '/admin/guide-results': {
    title: 'PM Guide Results',
    subtitle:
      'Review external tester guide submissions — exceptions first, with evidence links and a durable review trail.',
  },
  '/admin/activity': {
    title: 'Activity Reports',
    subtitle: 'Generate investigator activity reports and monitor report-run completion status.',
  },
  '/admin/activity/reports/:id': {
    title: 'Activity Report Viewer',
    subtitle: 'Review the generated investigator artifact inside DHA PRIME.',
  },
  '/admin/config/report-registry': {
    title: 'Report Registry',
    subtitle: 'Approved report templates and the products generated from them.',
  },
});

export function getStaticPageMetadata(pathname) {
  if (STATIC_PAGE_METADATA[pathname]) return STATIC_PAGE_METADATA[pathname];
  for (const [pattern, meta] of Object.entries(STATIC_PAGE_METADATA)) {
    if (!pattern.includes(':')) continue;
    if (matchPath({ path: pattern, end: true }, pathname)) return meta;
  }
  return null;
}
